// Browser files → the "local URI" the shared services/uploadService.js expects.
// A picked file becomes a blob: object URL; the expo-file-system shim reads it
// back when the service uploads. Images are compressed first with the same
// presets as the app (utils/imagePresets.js).
import { IMAGE_PRESETS } from "../../../utils/imagePresets";


const COMPRESS_TIMEOUT_MS = 20000;

// Opens the native file dialog. Resolves File[] (empty when cancelled).
export function pickFiles({ accept = "image/*", multiple = false } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = multiple;
    input.addEventListener("change", () => resolve(Array.from(input.files || [])), { once: true });
    input.addEventListener("cancel", () => resolve([]), { once: true });
    input.click();
  });
}

export const isLocalUri = (uri) => typeof uri === "string" && /^(blob|data):/i.test(uri);

function toObjectUri(blob) {
  return URL.createObjectURL(blob);
}

export function releaseUri(uri) {
  if (typeof uri === "string" && uri.startsWith("blob:")) URL.revokeObjectURL(uri);
}

// Resize to preset.maxWidth (never upscale) and re-encode as JPEG, like
// expo-image-manipulator in utils/imageUtils.js. Resolves a blob: URI.
async function compressImageFile(file, preset = IMAGE_PRESETS.upload) {
  if (!file || !/^image\//.test(file.type)) throw new Error("Choose an image file.");
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("This photo took too long to process. Try a different or smaller photo.")), COMPRESS_TIMEOUT_MS);
  });
  try {
    return await Promise.race([encode(file, preset), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function encode(file, { maxWidth, quality }) {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, maxWidth / bitmap.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; // = --white (canvas needs a literal). JPEG has no alpha: flatten transparent PNGs onto white
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not process this photo."))), "image/jpeg", quality)
  );
  return toObjectUri(blob);
}

export const compressForUpload = (file) => compressImageFile(file, IMAGE_PRESETS.upload);
export const compressForEvent = (file) => compressImageFile(file, IMAGE_PRESETS.event);
export const compressForAvatar = (file) => compressImageFile(file, IMAGE_PRESETS.avatar);

// PDFs are uploaded as-is (uploadPDFViaREST checks the .pdf name and 25 MB cap).
export function pdfToAsset(file) {
  return { uri: toObjectUri(file), name: file.name, size: file.size, mimeType: file.type };
}
